import { Alert, Box, Divider, Stack, TextField, Typography } from '@mui/material';
import { FC } from 'react';
import { EducationSection } from '../hooks/usePatientEducation';

const PATIENT_EDUCATION_TITLE_MAX_LENGTH = 150;

type Props = {
  sections: EducationSection[];
  onSectionsChange: (sections: EducationSection[]) => void;
  disabled?: boolean;
  errorMessage?: string | null;
  helperText?: string;
};

const DEFAULT_HELPER_TEXT =
  'Review and edit the content below. Use markdown formatting: "#", "##" for section headers, "- " for bullet points.';

export const PatientEducationSectionsEditor: FC<Props> = ({
  sections,
  onSectionsChange,
  disabled,
  errorMessage,
  helperText = DEFAULT_HELPER_TEXT,
}) => {
  return (
    <Stack spacing={2}>
      <Alert severity="warning">
        This content was generated by AI and may contain mistakes. Review it carefully before approving.
      </Alert>
      <Typography variant="body2" color="text.secondary">
        {helperText}
      </Typography>
      {sections.map((section, idx) => (
        <Box key={section.icdCode} sx={{ mb: idx < sections.length - 1 ? 1 : 0 }}>
          <TextField
            label="Title"
            value={section.patientTitle}
            onChange={(e) => {
              const next = [...sections];
              next[idx] = {
                ...next[idx],
                patientTitle: e.target.value.replace(/[\r\n]+/g, ' '),
              };
              onSectionsChange(next);
            }}
            fullWidth
            size="small"
            sx={{ mb: 1 }}
            disabled={disabled}
            inputProps={{ maxLength: PATIENT_EDUCATION_TITLE_MAX_LENGTH }}
            helperText={`${section.patientTitle.length}/${PATIENT_EDUCATION_TITLE_MAX_LENGTH} characters`}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            {section.icdCode} — {section.icdDescription}
          </Typography>
          <TextField
            value={section.content}
            onChange={(e) => {
              const next = [...sections];
              next[idx] = { ...next[idx], content: e.target.value };
              onSectionsChange(next);
            }}
            fullWidth
            multiline
            minRows={10}
            maxRows={20}
            disabled={disabled}
            sx={{ '& .MuiInputBase-root': { fontFamily: 'monospace', fontSize: 13 } }}
          />
          {idx < sections.length - 1 && <Divider sx={{ mt: 2 }} />}
        </Box>
      ))}
      {errorMessage && (
        <Typography color="error" variant="body2">
          {errorMessage}
        </Typography>
      )}
    </Stack>
  );
};
